// apps/tenant/src/lib/sloeAuthClient.ts
// Browser half of the Lab's SloeAuth sign-in (port of @sloe-os/auth-kit's
// sloeAuthClient.ts). Talks only to this server's own /auth/* routes with the
// session cookie; no provider logic and no secret ever lands in JS. The hosted
// SloeAuth page (auth.sloelabs.com) owns Google / email code.

const PREFIX = "/auth";

/** Navigate to SloeAuth. `returnTo` may be a path on this host or an absolute URL on a Lab host. */
export function signIn(returnTo: string = window.location.href): void {
  window.location.assign(`${PREFIX}/login?return_to=${encodeURIComponent(returnTo)}`);
}

/**
 * Sign out: the server revokes the SloeAuth refresh token FIRST, then clears the
 * cookie. Returns false when SloeAuth could not confirm the revocation — the
 * session is then still live on this device, and the caller should say so.
 * On success the browser is sent through SloeAuth's end-session page (so the
 * next "Sign in" asks who you are) and back to `returnTo`.
 */
export async function signOut(returnTo: string = window.location.origin + "/"): Promise<boolean> {
  let r: Response;
  try {
    r = await fetch(`${PREFIX}/logout`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ return_to: returnTo }),
    });
  } catch {
    return false;
  }
  if (!r.ok) return false;
  clearDataToken();
  let endSession: string | undefined;
  try { endSession = (await r.json())?.end_session_url; } catch { /* no body */ }
  window.location.assign(endSession || returnTo);
  return true;
}

/** Every call that spends the refresh token runs under one cross-tab lock (same origin). The token rotates on use,
 *  so two tabs refreshing at once would each present the same token. The server coordinates across hosts/workers. */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (typeof navigator !== "undefined" ? (navigator as any).locks : undefined) as
    | { request(name: string, cb: () => Promise<T>): Promise<T> }
    | undefined;
  return locks ? locks.request("sloe-lab-refresh", fn) : fn();
}
// Bounded, so a stalled request cannot hold the cross-tab lock (and every other tab's token) hostage.
const REFRESH_TIMEOUT_MS = 15_000;

/** A fresh 5-minute SloeData token for the Neon Data API (supabase-js `accessToken`). Cached until 30 s before expiry;
 *  concurrent callers share one in-flight request so a page load does not rotate the refresh token N times.
 *  `dataGen` makes clearDataToken() final: a request already in flight when it was called cannot refill the cache. */
let dataCache: { token: string; exp: number } | null = null;
let dataInflight: Promise<string | null> | null = null;
let dataGen = 0;

export async function getDataToken(): Promise<string | null> {
  if (dataCache && dataCache.exp - 30_000 > Date.now()) return dataCache.token;
  if (dataInflight) return dataInflight;
  const gen = dataGen;
  const slot: { p?: Promise<string | null> } = {};
  const p = (slot.p = (async () => {
    try {
      const r = await withRefreshLock(() =>
        fetch(`${PREFIX}/data-token`, {
          credentials: "same-origin",
          cache: "no-store",
          signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
        }),
      );
      if (!r.ok || gen !== dataGen) {
        if (gen === dataGen) dataCache = null;
        return null;
      }
      const j = await r.json();
      if (gen !== dataGen) return null;
      dataCache = { token: j.access_token, exp: Date.now() + Number(j.expires_in ?? 300) * 1000 };
      return dataCache.token;
    } catch {
      return null;
    } finally {
      if (dataInflight === slot.p) dataInflight = null;
    }
  })());
  dataInflight = p;
  return p;
}

export function clearDataToken(): void {
  dataGen++;
  dataCache = null;
  dataInflight = null;
}
